/**
 * URL-backed selection + group-by + sort state for the unified Tests
 * page (#542).
 *
 * The Tests page mixes two state surfaces:
 *
 *   - **URL-backed** (this hook): selected entity, group-by
 *     dimension, sort. These are share-link relevant — pasting a
 *     URL should land another user on the same view of the same
 *     test/suite. Matches the audit page's convention
 *     (`pages/Admin/Audit/index.tsx`).
 *   - **localStorage-backed** (separate `usePersistedState` calls
 *     in the sidebar component): filter dropdowns and view-mode
 *     toggles. These are muscle memory — the user wants their
 *     usual filter set to survive page reloads but doesn't want
 *     to clutter shareable URLs.
 *
 * The `Selection` discriminator round-trips as a single
 * `?selected=test-<id>` or `?selected=suite-<id>` param. The kind
 * prefix is what lets the right-pane swap fire correctly. An empty
 * or malformed value → `null` (the page renders an empty-state
 * "select a test or suite from the sidebar" prompt).
 */
import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { GroupBy, Selection, SortBy } from './types'

export const DEFAULT_GROUP_BY: GroupBy = 'suite'
export const DEFAULT_SORT_BY: SortBy = 'priority'

/** Exported for direct unit-testing (`__tests__/useTestsSelection.test.ts`).
 * The hook itself wraps these for `useSearchParams` integration. */
export function parseSelection(raw: string | null): Selection {
  if (!raw) return null
  if (raw.startsWith('test-')) {
    const id = raw.slice('test-'.length)
    return id ? { kind: 'test', id } : null
  }
  if (raw.startsWith('suite-')) {
    const id = raw.slice('suite-'.length)
    return id ? { kind: 'suite', id } : null
  }
  return null
}

export function serializeSelection(sel: Selection): string | null {
  if (!sel) return null
  return `${sel.kind}-${sel.id}`
}

export function parseGroupBy(raw: string | null): GroupBy {
  if (raw === 'none' || raw === 'status' || raw === 'intent' || raw === 'suite') return raw
  return DEFAULT_GROUP_BY
}

export function parseSortBy(raw: string | null): SortBy {
  if (raw === 'priority' || raw === 'status' || raw === 'name' || raw === 'newest') return raw
  return DEFAULT_SORT_BY
}

/** The two lifecycle stages the Tests page shows (#1827). `proposed`
 * is the candidate tests the proposer extracted (from reference docs,
 * and any future source) awaiting authoring; `active` is today's
 * authored-tests browser, unchanged. */
export type TestsStage = 'proposed' | 'active'

/** Parse the `?stage=` query value to a known stage. The param is a
 * stable value decoupled from the UI labels, so renaming the tab text
 * never changes the URL contract. Defaults to `active` — today's
 * authored browser is the landing view — and an unknown / missing
 * value falls back there so a stale bookmark never breaks the page.
 * Sits beside `parseSelection` / `parseGroupBy` / `parseSortBy` as
 * another Tests-page URL parser. Exported for unit tests. */
export function parseStage(raw: string | null): TestsStage {
  return raw === 'proposed' ? 'proposed' : 'active'
}

export interface TestsSelectionState {
  selection: Selection
  groupBy: GroupBy
  sortBy: SortBy
  setSelection: (next: Selection) => void
  setGroupBy: (next: GroupBy) => void
  setSortBy: (next: SortBy) => void
}

export function useTestsSelection(): TestsSelectionState {
  const [searchParams, setSearchParams] = useSearchParams()

  const selection = useMemo(() => parseSelection(searchParams.get('selected')), [searchParams])
  const groupBy = useMemo(() => parseGroupBy(searchParams.get('group')), [searchParams])
  const sortBy = useMemo(() => parseSortBy(searchParams.get('sort')), [searchParams])

  const setSelection = useCallback(
    (next: Selection) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          const serialized = serializeSelection(next)
          if (serialized) out.set('selected', serialized)
          else out.delete('selected')
          return out
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setGroupBy = useCallback(
    (next: GroupBy) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          if (next === DEFAULT_GROUP_BY) out.delete('group')
          else out.set('group', next)
          return out
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setSortBy = useCallback(
    (next: SortBy) => {
      setSearchParams(
        (prev) => {
          const out = new URLSearchParams(prev)
          if (next === DEFAULT_SORT_BY) out.delete('sort')
          else out.set('sort', next)
          return out
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  return { selection, groupBy, sortBy, setSelection, setGroupBy, setSortBy }
}
